import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withFormik } from 'formik';
import { inject, observer } from 'mobx-react';

import api from '../utils/api';
import InputText from './InputText';
import { FONT_SIZE_BIG, FONT_SIZE_MEDIUM } from '../constants';
import Row from './Row';
import Textarea from './Textarea';
import InputLinkOrFile from './InputLinkOrFile';
import {
  crossScreenFontSize,
  hoverDefault,
  lineThroughHover,
  resetButton,
  toVw,
  toVwMobile,
  toVwTablet,
} from '../utils/styles';
import InputPhone from './InputPhone';
import mq from '@/utils/media-queries';

const STATUS_SENT = 'sent';
const STATUS_SENDING = 'sending';

const Styled = styled.div`
  width: ${toVw(788)};

  ${mq.tablet(css`
    width: 100%;
  `)};
`;

const TITLE_MARGIN_BOTTOM = 40;

const Title = styled.div`
	${crossScreenFontSize(FONT_SIZE_BIG)}
	margin-bottom: ${toVw(TITLE_MARGIN_BOTTOM)};

	${mq.tablet(css`
    margin-bottom: ${toVwTablet(TITLE_MARGIN_BOTTOM)};
  `)};

	${mq.mobile(css`
    margin-bottom: ${toVwMobile(TITLE_MARGIN_BOTTOM)};
  `)};
`;

const INPUTS_MARGIN_BOTTOM = 89;

const Inputs = styled.div`
  margin-bottom: ${toVw(INPUTS_MARGIN_BOTTOM)};

  ${props =>
    props.disabled &&
    css`
      opacity: 0.2;
      pointer-events: none;
    `};

  ${mq.tablet(css`
    margin-bottom: ${toVwTablet(INPUTS_MARGIN_BOTTOM)};
  `)};

  ${mq.mobile(css`
    margin-bottom: ${toVwMobile(INPUTS_MARGIN_BOTTOM)};
  `)};
`;

const FORM_ROW_MARGIN_BOTTOM = 72;

const FormRow = styled(Row)`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${toVw(FORM_ROW_MARGIN_BOTTOM)};

  &:last-child {
    margin-bottom: 0;
  }

  ${mq.tablet(css`
    margin-bottom: ${toVwTablet(FORM_ROW_MARGIN_BOTTOM)};
  `)};

  ${mq.mobile(css`
    display: block;
    margin-bottom: ${toVwMobile(FORM_ROW_MARGIN_BOTTOM)};
  `)};
`;

const Col = styled.div`
  box-sizing: border-box;
  flex-basis: 45.2%;

  ${mq.tablet(css`
    flex-basis: 42.8%;
  `)};

  ${mq.mobile(css`
    margin-bottom: ${toVwMobile(FORM_ROW_MARGIN_BOTTOM)};

    &:last-child {
      margin-bottom: 0;
    }
  `)};
`;

const StyledInputText = styled(InputText)`
  width: 100%;
`;

const StyledInputPhone = styled(InputPhone)`
  width: 100%;
`;

const StyledInputFile = styled(InputLinkOrFile)`
  width: 100%;
`;

const TEXTAREA_MARGIN_TOP = 22;

const StyledTextarea = styled(Textarea)`
  margin-top: ${toVw(TEXTAREA_MARGIN_TOP)};

  ${mq.tablet(css`
    margin-top: ${toVwTablet(TEXTAREA_MARGIN_TOP)};
  `)};

  ${mq.mobile(css`
    margin-top: ${toVwMobile(33)};
  `)};
`;

const Submit = styled.button`
  ${hoverDefault};
  ${resetButton};
  font-weight: 300;
  cursor: pointer;
  color: ${props => props.theme.colorAccent};
  ${crossScreenFontSize(FONT_SIZE_MEDIUM)};
  ${props => lineThroughHover(props.theme.colorAccent)};

  ${props =>
    props.disabled &&
    css`
      pointer-events: none;
      cursor: default;
    `};
`;

const InnerForm = ({ values, errors, handleSubmit, handleChange, setFieldValue, status }) => {
  return (
    <Styled>
      <Title>Я в деле</Title>
      <form onSubmit={handleSubmit} method="POST">
        <Inputs disabled={status === STATUS_SENT || status === STATUS_SENDING}>
          <FormRow>
            <Col>
              <StyledInputText
                name="f_name"
                placeholder="Имя"
                data-test="input-name"
                onChange={handleChange}
                value={values.f_name}
                _error={String(errors.name)}
              />
            </Col>
          </FormRow>
          <FormRow>
            <Col>
              <StyledInputPhone
                name="f_phone"
                placeholder="Телефон"
                data-test="input-phone"
                onChange={handleChange}
                value={values.f_phone}
                _error={String(errors.phone)}
              />
            </Col>
            <Col>
              <StyledInputText
                name="f_email"
                type="email"
                placeholder="Email"
                data-test="input-email"
                onChange={handleChange}
                value={values.f_email}
                _error={String(errors.email)}
              />
            </Col>
          </FormRow>
          <FormRow>
            <Col>
              <StyledInputFile
                nameInputText="f_portfolio_link"
                nameInputFile="f_portfolio_file"
                placeholder="Портфолио"
                dataTest="input-portfolio-link"
                onChange={handleChange}
                valueInputText={values.f_portfolio_link}
                valueInputFile={values.f_portfolio_file}
                setFieldValue={setFieldValue}
                _error={String(errors.portfolio)}
              />
            </Col>
            <Col>
              <StyledInputFile
                nameInputText="f_cv_link"
                nameInputFile="f_cv_file"
                placeholder="Резюме"
                dataTest="input-cv-link"
                onChange={handleChange}
                valueInputText={values.f_cv_link}
                valueInputFile={values.f_cv_file}
                setFieldValue={setFieldValue}
                _error={String(errors.cv)}
              />
            </Col>
          </FormRow>
          <StyledTextarea
            name="f_about"
            onChange={handleChange}
            value={values.f_about}
            placeholder="Немного о себе"
            data-test="input-about"
            _error={String(errors.about)}
          />
          <input type="hidden" name="f_vacancy_id" value={values.f_vacancy_id} />
        </Inputs>
        <Submit data-test="submit" disabled={status === STATUS_SENT}>
          {status === STATUS_SENT ? 'спасибо!' : 'Отправить'}
        </Submit>
      </form>
    </Styled>
  );
};

InnerForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  vacancyId: PropTypes.string.isRequired,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  status: PropTypes.any,
};

const VacancyForm = withFormik({
  mapPropsToValues: props => {
    return {
      f_name: '',
      f_email: '',
      f_portfolio_link: '',
      f_portfolio_file: '',
      f_cv_link: '',
      f_cv_file: '',
      f_about: '',
      f_vacancy_id: props.vacancyId,
    };
  },
  validate: values => {
    const errors = {};

    if (!values.f_name) {
      errors.name = true;
    }

    if (!values.f_phone || !/^(\+\d\(\d{3}\) \d{3} \d{2} \d{2})$/i.test(values.f_phone)) {
      errors.phone = true;
    }

    if (!values.f_email || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.f_email)) {
      errors.email = true;
    }

    if (!values.f_portfolio_link && !values.f_portfolio_file) {
      errors.portfolio = true;
    }

    if (!values.f_cv_link && !values.f_cv_file) {
      errors.cv = true;
    }

    if (values.f_vacancy_id === undefined) {
      console.error('Form was not sent, because vacancy id is not defined.');
    }

    return errors;
  },
  handleSubmit: (values, { setStatus, props: { progressStore, status } }) => {
    if (status === STATUS_SENT) {
      return;
    }

    progressStore.turnOn();

    setStatus(STATUS_SENDING);

    values = {
      ...values,
      catalogue: 4,
      cc: 57,
      sub: 57,
      posting: 1,
      f_Checked: 1,
      isNaked: 1,
      nc_token: '584c57e113e76778b0700646510b9a97',
    };

    api
      .sendVacancyForm(values)
      .then(() => {
        return new Promise(resolve => {
          setTimeout(() => {
            setStatus(STATUS_SENT);
            resolve();
          }, 1000);
        });
      })
      .catch(err => console.error(err))
      .then(() => {
        progressStore.turnOff();
      });
  },
})(InnerForm);

export default inject(stores => ({ progressStore: stores.store.progress }))(observer(VacancyForm));



// WEBPACK FOOTER //
// ./src/components/VacancyForm.js